/**
 * JSON-LD STRUCTURED DATA BUILDERS
 *
 * These power both classic rich results (Google) and generative engine
 * optimisation / GEO (ChatGPT, Perplexity, Gemini, AI Overviews), which lean
 * heavily on explicit entity markup rather than inferring meaning from layout.
 *
 * Every page emits a single <script type="application/ld+json"> containing an
 * @graph, so entities can cross-reference each other by @id.
 *
 * WORDPRESS NOTE: when porting, these map cleanly onto Yoast/RankMath schema
 * filters, or can be emitted verbatim from a PHP partial using ACF values.
 */

import { SITE, PRACTITIONER, SOCIAL_PROFILES, absoluteUrl } from './siteConfig'

type Json = Record<string, unknown>

/** Stable @id anchors so entities can reference each other. */
export const ID = {
  organization: `${SITE.url}/#organization`,
  website: `${SITE.url}/#website`,
  person: `${SITE.url}/#branka`,
  page: (path: string) => `${absoluteUrl(path)}#webpage`,
}

/**
 * The business itself. HealthAndBeautyBusiness is the closest accurate
 * LocalBusiness subtype for a wellbeing practice — deliberately NOT a medical
 * type, since these are complementary, non-clinical services.
 */
export function organizationSchema(): Json {
  return {
    '@type': ['Organization', 'HealthAndBeautyBusiness'],
    '@id': ID.organization,
    name: SITE.name,
    url: SITE.url,
    email: SITE.email,
    description: SITE.description,
    slogan: SITE.tagline,
    priceRange: SITE.priceRange,
    areaServed: SITE.areaServed,
    logo: {
      '@type': 'ImageObject',
      url: SITE.logo,
      caption: SITE.name,
    },
    image: SITE.defaultImage,
    founder: { '@id': ID.person },
    knowsAbout: PRACTITIONER.knowsAbout,
    sameAs: SOCIAL_PROFILES.map((profile) => profile.url),
    contactPoint: {
      '@type': 'ContactPoint',
      email: SITE.email,
      contactType: 'Customer enquiries',
      availableLanguage: ['English', 'Slovenian'],
    },
  }
}

/** The practitioner as a distinct, linkable entity — key for AI attribution. */
export function personSchema(): Json {
  return {
    '@type': 'Person',
    '@id': ID.person,
    name: PRACTITIONER.name,
    alternateName: PRACTITIONER.alternateName,
    jobTitle: PRACTITIONER.jobTitle,
    description: PRACTITIONER.bio,
    image: PRACTITIONER.image,
    url: absoluteUrl('/meet-milangela'),
    email: SITE.email,
    knowsAbout: PRACTITIONER.knowsAbout,
    worksFor: { '@id': ID.organization },
    hasCredential: PRACTITIONER.credentials.map((credential) => ({
      '@type': 'EducationalOccupationalCredential',
      name: credential,
    })),
    sameAs: SOCIAL_PROFILES.map((profile) => profile.url),
  }
}

export function websiteSchema(): Json {
  return {
    '@type': 'WebSite',
    '@id': ID.website,
    url: SITE.url,
    name: SITE.name,
    description: SITE.description,
    inLanguage: SITE.language,
    publisher: { '@id': ID.organization },
  }
}

export function webPageSchema(options: {
  path: string
  title: string
  description: string
  image?: string
  datePublished?: string
  dateModified?: string
}): Json {
  return {
    '@type': 'WebPage',
    '@id': ID.page(options.path),
    url: absoluteUrl(options.path),
    name: options.title,
    description: options.description,
    inLanguage: SITE.language,
    isPartOf: { '@id': ID.website },
    about: { '@id': ID.organization },
    primaryImageOfPage: options.image
      ? { '@type': 'ImageObject', url: options.image }
      : undefined,
    datePublished: options.datePublished,
    dateModified: options.dateModified,
  }
}

/** Breadcrumbs give both crawlers and AI answers a clear site hierarchy. */
export function breadcrumbSchema(
  trail: Array<{ name: string; path: string }>,
): Json {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: trail.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: absoluteUrl(crumb.path),
    })),
  }
}

/** A bookable offering. Used on the three service detail pages. */
export function serviceSchema(options: {
  name: string
  description: string
  path: string
  image?: string
  price?: string
  priceCurrency?: string
  serviceType?: string
}): Json {
  return {
    '@type': 'Service',
    '@id': `${absoluteUrl(options.path)}#service`,
    name: options.name,
    description: options.description,
    url: absoluteUrl(options.path),
    image: options.image,
    serviceType: options.serviceType ?? options.name,
    category: 'Wellbeing',
    provider: { '@id': ID.organization },
    areaServed: SITE.areaServed,
    availableChannel: [
      {
        '@type': 'ServiceChannel',
        name: 'Online session',
        serviceUrl: absoluteUrl('/lets-connect'),
      },
      {
        '@type': 'ServiceChannel',
        name: 'In-person session',
        serviceUrl: absoluteUrl('/lets-connect'),
      },
    ],
    offers: options.price
      ? {
          '@type': 'Offer',
          price: options.price,
          priceCurrency: options.priceCurrency ?? 'EUR',
          availability: 'https://schema.org/InStock',
          url: absoluteUrl('/lets-connect'),
        }
      : undefined,
  }
}

/** Journal posts. `articleBody` materially improves AI answer extraction. */
export function articleSchema(options: {
  title: string
  description: string
  path: string
  image?: string
  datePublished: string
  dateModified?: string
  section?: string
  body?: string
  wordCount?: number
}): Json {
  return {
    '@type': 'BlogPosting',
    '@id': `${absoluteUrl(options.path)}#article`,
    headline: options.title,
    description: options.description,
    url: absoluteUrl(options.path),
    image: options.image,
    datePublished: options.datePublished,
    dateModified: options.dateModified ?? options.datePublished,
    articleSection: options.section,
    articleBody: options.body,
    wordCount: options.wordCount,
    inLanguage: SITE.language,
    author: { '@id': ID.person },
    publisher: { '@id': ID.organization },
    isPartOf: { '@id': ID.page(options.path) },
    mainEntityOfPage: { '@id': ID.page(options.path) },
  }
}

/** Blog index listing. */
export function blogSchema(
  posts: Array<{ title: string; path: string; datePublished: string }>,
): Json {
  return {
    '@type': 'Blog',
    '@id': `${absoluteUrl('/journal')}#blog`,
    name: `The Garden — Journal | ${SITE.name}`,
    url: absoluteUrl('/journal'),
    inLanguage: SITE.language,
    publisher: { '@id': ID.organization },
    blogPost: posts.map((post) => ({
      '@type': 'BlogPosting',
      headline: post.title,
      url: absoluteUrl(post.path),
      datePublished: post.datePublished,
      author: { '@id': ID.person },
    })),
  }
}

/**
 * Standalone reviews. Deliberately NOT aggregateRating — Google requires a
 * rating value for that, and these testimonials carry no star ratings.
 * Fabricating ratings risks a structured-data manual action.
 */
export function reviewSchema(
  reviews: Array<{ author: string; body: string; location?: string }>,
): Json[] {
  return reviews.map((review) => ({
    '@type': 'Review',
    itemReviewed: { '@id': ID.organization },
    author: {
      '@type': 'Person',
      name: review.author,
      ...(review.location ? { address: review.location } : {}),
    },
    reviewBody: review.body,
    publisher: { '@id': ID.organization },
  }))
}

/**
 * FAQPage — the single highest-leverage schema for GEO. AI engines quote
 * question/answer pairs almost verbatim.
 */
export function faqSchema(
  faqs: Array<{ question: string; answer: string }>,
): Json {
  return {
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  }
}

/** Podcast / meditation media pages. */
export function audioVideoSchema(options: {
  type: 'PodcastSeries' | 'VideoObject'
  name: string
  description: string
  path: string
  image?: string
  externalUrl?: string
  uploadDate?: string
}): Json {
  const base: Json = {
    '@type': options.type,
    name: options.name,
    description: options.description,
    url: absoluteUrl(options.path),
    image: options.image,
    inLanguage: SITE.language,
  }
  if (options.type === 'PodcastSeries') {
    return {
      ...base,
      webFeed: options.externalUrl,
      author: { '@id': ID.person },
    }
  }
  return {
    ...base,
    thumbnailUrl: options.image,
    contentUrl: options.externalUrl,
    embedUrl: options.externalUrl,
    uploadDate: options.uploadDate,
    publisher: { '@id': ID.organization },
  }
}

/** Recursively strips undefined values so the emitted JSON-LD stays clean. */
export function pruneSchema<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((item) => pruneSchema(item)) as unknown as T
  }
  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, entryValue]) => entryValue !== undefined)
      .map(([key, entryValue]) => [key, pruneSchema(entryValue)])
    return Object.fromEntries(entries) as T
  }
  return value
}
